import { CheckState } from '../../enum/check-state.enum';
import { SearchIndex } from './search-index';
import { SearchMatcher } from './search-matcher';

export interface EntryNodeInit<T> {
  readonly id: unknown;
  readonly entity?: T;
  readonly synthetic: boolean;
  readonly parent?: EntryNode<T>;
  readonly searchIndex: SearchIndex;
  readonly label: string;
  readonly icon?: string;
}

export class EntryNode<T> {
  public readonly id: unknown;
  public readonly entity?: T;
  public readonly synthetic: boolean;
  public readonly parent?: EntryNode<T>;
  public readonly searchIndex: SearchIndex;

  public readonly label: string;
  readonly icon?: string;

  public children: readonly EntryNode<T>[] = [];

  private _expanded = false;
  private _ownCheck = false;
  private _matchesSearch = true;
  private _matchesSelection = true;

  public constructor(init: EntryNodeInit<T>) {
    this.id = init.id;
    this.entity = init.entity;
    this.synthetic = init.synthetic;
    this.parent = init.parent;
    this.searchIndex = init.searchIndex;
    this.label = init.label;
    this.icon = init.icon;
  }

  public isLeaf(): boolean {
    return this.children.length === 0;
  }

  public get checkState(): CheckState {
    if (this.isLeaf()) {
      return this._ownCheck ? CheckState.check : CheckState.uncheck;
    }
    const states = this.children
      .filter((item) => item.visible)
      .map((item) => item.checkState);
    if (states.every((state) => state === CheckState.check)) {
      return CheckState.check;
    }
    if (states.every((state) => state === CheckState.uncheck)) {
      return CheckState.uncheck;
    }
    return CheckState.partial;
  }

  public get visible(): boolean {
    return (
      (this._matchesSearch && this._matchesSelection) ||
      this.children.some((item) => item.visible)
    );
  }

  public isExpanded(): boolean {
    return this._expanded;
  }

  public check(): void {
    if (!this.visible) {
      return;
    }
    if (this.isLeaf()) {
      this._ownCheck = true;
    } else {
      this.children.forEach((item) => item.check());
    }
  }

  public uncheck(): void {
    if (this.isLeaf()) {
      this._ownCheck = false;
    } else {
      this.children.forEach((item) => item.uncheck());
    }
  }

  public toggleCheck(): void {
    if (this.checkState === CheckState.check) {
      this.uncheck();
    } else {
      this.check();
    }
  }

  public expand(): void {
    this._expanded = true;
  }

  public collapse(): void {
    this._expanded = false;
  }

  public toggleExpand(): void {
    this._expanded = !this._expanded;
  }

  public setMatches(value: boolean): void {
    this._matchesSearch = value;
  }

  public toggleVisibilityByCheckState(onlySelected: boolean) {
    this._matchesSelection = onlySelected
      ? this.checkState !== CheckState.uncheck
      : true;

    if (this._matchesSearch) {
      this.children.forEach((item) =>
        item.toggleVisibilityByCheckState(onlySelected),
      );
    }
  }

  public getCheckedLeafEntities(): T[] {
    if (this.children.length === 0) {
      return this.checkState === CheckState.check && this.entity
        ? [this.entity]
        : [];
    }
    return this.children.flatMap((child) => child.getCheckedLeafEntities());
  }

  public getCheckedGroups() {
    if (this.checkState === CheckState.uncheck) {
      return [];
    }

    if (
      this.checkState === CheckState.check &&
      !this.synthetic &&
      this.entity
    ) {
      return [this.entity];
    }

    return this.children.flatMap((child) => child.getCheckedGroups());
  }

  public checkEntities(entities: ReadonlySet<T>): void {
    if (this.isLeaf()) {
      if (this.entity !== undefined && entities.has(this.entity)) {
        this.check();
      } else {
        this.uncheck();
      }
      return;
    }
    this.children.forEach((child) => child.checkEntities(entities));
  }

  public applySearch(
    query: string,
    matcher: SearchMatcher,
    parentMatches: boolean,
  ): void {
    const matches = parentMatches || matcher.matches(this.searchIndex, query);
    this._matchesSearch = matches;
    this.children.forEach((child) =>
      child.applySearch(query, matcher, matches),
    );
  }

  public containsId(id: unknown): boolean {
    return (
      this.id === id || this.children.some((child) => child.containsId(id))
    );
  }
}
